import {
  ActionIcon,
  Collapse,
  Grid,
  Group,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useState } from 'react';
import { PiCaretDown, PiCaretUp } from 'react-icons/pi';
import { ClientType } from '@/types';
import JurisdictionsSelect from '@/components/JurisdictionsSelect/JurisdictionsSelect';
import {
  useRecipientType,
  useIndividualDetails,
  useCorporateDetails,
  useManualInvoiceActions,
} from '@/models/manual-invoice.store';

export default function RecipientSection() {
  const [expanded, setExpanded] = useState(true);
  const recipientType = useRecipientType();
  const individualDetails = useIndividualDetails();
  const corporateDetails = useCorporateDetails();
  const { setRecipientType, updateIndividualField, updateCorporateField } =
    useManualInvoiceActions();

  const getSummary = () => {
    if (recipientType === ClientType.Individual) {
      const name = individualDetails.fullName || 'No name';
      const address = individualDetails.legalAddress;
      return address ? `${name} · ${address}` : name;
    }
    const name = corporateDetails.companyName || 'No company';
    const address = corporateDetails.companyLegalAddress;
    return address ? `${name} · ${address}` : name;
  };

  return (
    <Paper p="md" withBorder>
      <Group
        justify="space-between"
        style={{ cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
        mb={expanded ? 'md' : 0}
      >
        <Group gap="xs">
          <Text size="xs" fw={600} c="dimmed" tt="uppercase">
            Bill To
          </Text>
          {!expanded && (
            <Text size="sm" c="dimmed" lineClamp={1}>
              — {getSummary()}
            </Text>
          )}
        </Group>
        <ActionIcon variant="subtle" size="sm">
          {expanded ? <PiCaretUp size={16} /> : <PiCaretDown size={16} />}
        </ActionIcon>
      </Group>

      <Collapse expanded={expanded}>
        <Stack gap="md">
          <SegmentedControl
            size="xs"
            value={recipientType}
            onChange={(value) => setRecipientType(value as ClientType)}
            data={[
              { value: ClientType.Individual, label: 'Individual' },
              { value: ClientType.Corporate, label: 'Corporate' },
            ]}
          />

          {recipientType === ClientType.Individual ? (
            <Grid gap="xs">
              <Grid.Col span={6}>
                <TextInput
                  size="xs"
                  label="Full Name"
                  placeholder="Enter full name"
                  value={individualDetails.fullName}
                  onChange={(e) =>
                    updateIndividualField('fullName', e.target.value)
                  }
                  required
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  size="xs"
                  label="Document Number"
                  placeholder="Passport or ID"
                  value={individualDetails.documentNumber}
                  onChange={(e) =>
                    updateIndividualField('documentNumber', e.target.value)
                  }
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <JurisdictionsSelect
                  label="Nationality"
                  value={individualDetails.nationality || undefined}
                  onChange={(value) =>
                    updateIndividualField('nationality', value || '')
                  }
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <JurisdictionsSelect
                  label="Residence Country"
                  value={individualDetails.residenceCountry || undefined}
                  onChange={(value) =>
                    updateIndividualField('residenceCountry', value || '')
                  }
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <TextInput
                  size="xs"
                  label="Legal Address"
                  placeholder="Enter legal address"
                  value={individualDetails.legalAddress}
                  onChange={(e) =>
                    updateIndividualField('legalAddress', e.target.value)
                  }
                />
              </Grid.Col>
            </Grid>
          ) : (
            <Grid gap="xs">
              <Grid.Col span={6}>
                <TextInput
                  size="xs"
                  label="Company Name"
                  placeholder="Enter company name"
                  value={corporateDetails.companyName}
                  onChange={(e) =>
                    updateCorporateField('companyName', e.target.value)
                  }
                  required
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  size="xs"
                  label="Registration Number"
                  placeholder="Company reg. number"
                  value={corporateDetails.companyRegNumber}
                  onChange={(e) =>
                    updateCorporateField('companyRegNumber', e.target.value)
                  }
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  size="xs"
                  label="Tax ID / VAT Number"
                  placeholder="Tax ID or VAT"
                  value={corporateDetails.companyTaxId}
                  onChange={(e) =>
                    updateCorporateField('companyTaxId', e.target.value)
                  }
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  size="xs"
                  label="Legal Address"
                  placeholder="Company address"
                  value={corporateDetails.companyLegalAddress}
                  onChange={(e) =>
                    updateCorporateField('companyLegalAddress', e.target.value)
                  }
                />
              </Grid.Col>
            </Grid>
          )}
        </Stack>
      </Collapse>
    </Paper>
  );
}
